import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { endpoints, type Colleague } from "../lib/api";

/** Type a name or an email and pick from the colleagues who use the app: the
 * people being added show as chips. Used by Share and by projects. Enter with
 * nothing typed calls `onSubmit`. */
export function PeopleField({ chosen, onChange, taken, domain, onSubmit, autoFocus, label }: {
  chosen: string[];
  onChange: (emails: string[]) => void;
  /** Emails not to suggest: they're in already. */
  taken: Set<string>;
  /** A name typed on its own becomes name@domain. */
  domain: string;
  onSubmit?: () => void;
  autoFocus?: boolean;
  label: string;
}) {
  const colleagues = useQuery({ queryKey: ["colleagues"], queryFn: endpoints.colleagues, staleTime: 5 * 60_000 });
  const [typed, setTyped] = useState("");
  const [highlight, setHighlight] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  const suggestions = useMemo(
    () => suggest(colleagues.data ?? [], typed, new Set([...taken, ...chosen])),
    [colleagues.data, typed, taken, chosen],
  );

  /** A suggestion, or what was typed: an email, or a name on its own. */
  function add(email?: string) {
    let value = (email ?? suggestions[highlight]?.email ?? typed).trim().toLowerCase();
    if (!value) return;
    if (!value.includes("@") && domain) value = `${value}@${domain}`;
    if (!chosen.includes(value)) onChange([...chosen, value]);
    setTyped("");
    setHighlight(0);
    input.current?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === "," || event.key === "Tab") {
      if (typed.trim()) {
        event.preventDefault();
        add();
      } else if (event.key === "Enter" && chosen.length && onSubmit) {
        event.preventDefault();
        onSubmit();
      }
    } else if (event.key === "Backspace" && !typed && chosen.length) {
      onChange(chosen.slice(0, -1));
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
    }
  }

  return (
    <div className="people-field">
      <div className="share-field" onClick={() => input.current?.focus()}>
        {chosen.map((email) => (
          <span key={email} className="share-chip">
            {email}
            <button type="button" onClick={() => onChange(chosen.filter((e) => e !== email))} aria-label={`Remove ${email}`}>
              ✕
            </button>
          </span>
        ))}
        <input
          ref={input}
          value={typed}
          autoFocus={autoFocus}
          onChange={(event) => {
            setTyped(event.target.value);
            setHighlight(0);
          }}
          onKeyDown={onKeyDown}
          placeholder={chosen.length ? "" : `Name or email @${domain || "company"}`}
          aria-label={label}
          spellCheck={false}
          autoComplete="off"
        />
      </div>
      {typed.trim() && suggestions.length > 0 && (
        <ul className="share-suggestions" role="listbox">
          {suggestions.map((person, i) => (
            <li key={person.email} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                className={i === highlight ? "on" : undefined}
                onMouseDown={(event) => {
                  event.preventDefault();
                  add(person.email);
                }}
              >
                <Initials name={person.name ?? person.email} />
                <span>
                  <strong>{person.name ?? person.email}</strong>
                  {person.name && <small>{person.email}</small>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Colleagues matching what's been typed, by name or email, leaving out
 * anyone who has access already or is about to. */
function suggest(colleagues: Colleague[], typed: string, taken: Set<string>): Colleague[] {
  const needle = typed.trim().toLowerCase();
  if (!needle) return [];
  return colleagues
    .filter((c) => !taken.has(c.email))
    .filter((c) => c.email.includes(needle) || (c.name ?? "").toLowerCase().split(/\s+/).some((w) => w.startsWith(needle))
      || (c.name ?? "").toLowerCase().startsWith(needle))
    .slice(0, 6);
}

export function Initials({ name }: { name: string }) {
  const words = name.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  const letters = ((words[0]?.[0] ?? "?") + (words.length > 1 ? words[words.length - 1][0] : "")).toUpperCase();
  return <span className="share-initials" aria-hidden>{letters}</span>;
}
